import { DateTime } from "luxon";

import { ACKNOWLEDGEMENT_LOOKBACK_MS, byLogOrder } from "@storyboard/dispatch-view/acknowledgement";
import type { OpenTask, TaskSubjectRef } from "@storyboard/dispatch-view/task";
import { pinOf, taskState, taskSubjectKey } from "@storyboard/dispatch-view/task";
import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import type { TaskSubjectKind, TaskSubjectWrite } from "@storyboard/web-client";
import { idx, rowsByIndex } from "@/store/store";
import type { TmsEventRow } from "@/store/types";
import type { DispatchItem } from "./dispatchItems";
import type { DispatchTableRow } from "./dispatchTableRow";

/** The collections {@link computeTaskFold} reads, for `useCollectionsCompute`. */
export const taskCollections = ["task", "taskPinBoard", "taskAcknowledgement"] as const;

/**
 * The workspace's open tasks, and the lookups the page reads them through.
 *
 * Only open tasks enter: a done task keeps nothing handled and sits on no shelf.
 */
export type TaskFold = {
  /** Every open task, oldest first. */
  open: readonly OpenTask[];
  /** Each open task by its id. */
  byTask: ReadonlyMap<string, OpenTask>;
  /** The open tasks naming each target, keyed `${subject}:${targetId}`. */
  bySubject: ReadonlyMap<string, readonly OpenTask[]>;
  /** The open tasks leading to each driver, keyed by the subjects' driver membership ids; never by a feed. */
  byDriver: ReadonlyMap<string, readonly OpenTask[]>;
};

/** No open task: the fold while tasks are off, and of a workspace with none. */
export const NO_TASKS: TaskFold = Object.freeze({
  open: Object.freeze([]),
  byTask: new Map(),
  bySubject: new Map(),
  byDriver: new Map(),
});

/**
 * Every subject kind a task may name.
 *
 * Written as a record the compiler checks against the generated union, so a kind the API adds fails to compile here.
 */
const TASK_SUBJECT_KINDS: ReadonlySet<string> = new Set(
  Object.keys({
    membership: true,
    feed_item: true,
    stop_event: true,
    form_submission: true,
    hos_clock_violation: true,
    route_assignment_event: true,
  } satisfies Record<TaskSubjectKind, true>),
);

/** Whether a string, such as a TMS event's subject, is a kind a task may name. */
function isTaskSubjectKind(subject: string): subject is TaskSubjectRef["subject"] {
  return TASK_SUBJECT_KINDS.has(subject);
}

/** The subject and target that name one message or TMS event, to a task and to an acknowledgement alike. */
export type ItemTarget = { subject: Exclude<TaskSubjectRef["subject"], "membership">; targetId: string };

/**
 * How a TMS event is named: its anchor, under the event's own subject; null for an event of a subject no task names.
 *
 * @param event - The event, whose id is its anchor's.
 */
export function eventTargetOf(event: TmsEventRow): ItemTarget | null {
  if (!isTaskSubjectKind(event.subject) || event.subject === "membership") return null;
  return { subject: event.subject, targetId: event.id };
}

/**
 * How a Dispatch item is named: a message as a `feed_item` by its id, an event as {@link eventTargetOf} names it.
 *
 * @param item - The message or event.
 */
export function itemTargetOf(item: DispatchItem): ItemTarget | null {
  if (item.source === "tmsEvent") return item.tmsEvent ? eventTargetOf(item.tmsEvent) : null;
  return { subject: "feed_item", targetId: item.id };
}

/** What a TMS event's Dispatch item id starts with, ahead of its anchor id. */
export const TMS_EVENT_ITEM_PREFIX = "tmsEvent:";

/** Whether a Dispatch item id names a TMS event rather than a message. */
export function isTmsEventItemId(itemId: string): boolean {
  return itemId.startsWith(TMS_EVENT_ITEM_PREFIX);
}

/**
 * The id a Dispatch item carries for a task's subject: the message's own, or `tmsEvent:<anchor>` for an event.
 */
export function taskItemIdOf(task: OpenTask): string | null {
  for (const { subject, targetId } of task.task.subjects) {
    if (subject === "feed_item") return targetId;
    if (subject !== "membership") return `${TMS_EVENT_ITEM_PREFIX}${targetId}`;
  }
  return null;
}

/**
 * Folds the workspace's tasks inside the acknowledgement lookback into the open ones and their lookups.
 *
 * Each task's state comes from its own acknowledgement rows and its pin from its pin rows. A task naming one target
 * twice, which storage allows, enters that target's list once, and a driver's once however many of its subjects lead
 * there.
 *
 * @param workspaceId - The workspace whose tasks are folded.
 * @param now - The clock the lookback is measured from, as epoch milliseconds.
 */
export function computeTaskFold(workspaceId: string, now: number): TaskFold {
  const oldest = DateTime.fromMillis(now).minus({ milliseconds: ACKNOWLEDGEMENT_LOOKBACK_MS });
  const open: OpenTask[] = [];
  for (const task of rowsByIndex("task", idx.tasksByWorkspace, workspaceId)) {
    // Past the lookback a task is out of view, as an item past it is handled by age.
    if (DateTime.fromISO(task.createdAt) < oldest) continue;
    if (taskState(rowsByIndex("taskAcknowledgement", idx.taskAcknowledgementsByTask, task.id)) === "done") continue;
    open.push({ task, pin: pinOf(rowsByIndex("taskPinBoard", idx.taskPinBoardsByTask, task.id)) });
  }
  // Oldest first, ties on the id, so the shelf's order is the same on every board and every reload.
  open.sort((a, b) => byLogOrder(a.task, b.task));

  const byTask = new Map<string, OpenTask>();
  const bySubject = new Map<string, OpenTask[]>();
  const byDriver = new Map<string, OpenTask[]>();
  for (const entry of open) {
    byTask.set(entry.task.id, entry);
    const subjectKeys = new Set(entry.task.subjects.map(({ subject, targetId }) => taskSubjectKey(subject, targetId)));
    for (const key of subjectKeys) appendTo(bySubject, key, entry);
    const drivers = new Set(entry.task.subjects.flatMap(({ workspaceMembershipId: id }) => (id === null ? [] : [id])));
    for (const driver of drivers) appendTo(byDriver, driver, entry);
  }
  return { open, byTask, bySubject, byDriver };
}

/** Appends to the list under a key, starting one when the key has none. */
function appendTo(map: Map<string, OpenTask[]>, key: string, entry: OpenTask): void {
  const list = map.get(key);
  if (list) list.push(entry);
  else map.set(key, [entry]);
}

/** What a new task is about: the message or event in context, or its driver. */
export type TaskAbout = "item" | "driver";

/**
 * The grain a row's task is named at once the dispatcher has chosen what it is about: the driver's grain for a task
 * about the driver, else the view's own.
 *
 * @param grain - The view's grain.
 * @param about - What the dispatcher chose the task to be about.
 */
export function taskGrainFor(grain: DispatchGrain, about: TaskAbout): DispatchGrain {
  return about === "driver" ? "driver" : grain;
}

/**
 * What a task made from a row is about.
 *
 * At the driver grain the driver, by membership, which no acknowledgement names, whatever the row. At the message
 * grain the message, or the event's anchor under the event's subject; a task's own row on the shelf stands for its
 * task's first subject, so the picker finds that task and offers to move it. Null for an event of a subject no task
 * can name.
 *
 * @param row - The row the task is made from.
 * @param grain - The grain the task is named at, from {@link taskGrainFor}.
 */
export function taskSubjectOfRow(row: DispatchTableRow, grain: DispatchGrain): TaskSubjectWrite | null {
  if (grain === "driver" || row.kind === "driver") {
    return { subject: "membership", targetId: row.driver.workspaceMembershipId };
  }
  if (row.kind === "task") {
    const [first] = row.task.task.subjects;
    return first === undefined ? null : { subject: first.subject, targetId: first.targetId };
  }
  return itemTargetOf(row.item);
}

/**
 * The open task already on a subject, the oldest when several name it; null when none does.
 *
 * @param target - The message or event, as {@link itemTargetOf} names it, or a driver by membership.
 * @param fold - The workspace's open tasks.
 */
export function openTaskOfTarget(target: TaskSubjectWrite, fold: TaskFold): OpenTask | null {
  return fold.bySubject.get(taskSubjectKey(target.subject, target.targetId))?.[0] ?? null;
}

/**
 * The open task already on a row's subject, which the picker offers to move rather than duplicate; for a driver's row
 * with no task of its own, the oldest open task on one of their messages or events; null when none.
 *
 * @param row - The row the picker opened on.
 * @param grain - The view's grain.
 * @param fold - The workspace's open tasks.
 */
export function openTaskOfRow(row: DispatchTableRow, grain: DispatchGrain, fold: TaskFold): OpenTask | null {
  if (row.kind === "task") return row.task;
  const subject = taskSubjectOfRow(row, grain);
  if (subject === null) return null;
  const own = fold.bySubject.get(taskSubjectKey(subject.subject, subject.targetId))?.[0] ?? null;
  if (own !== null || subject.subject !== "membership") return own;
  // A driver's row carries the chip of a task on one of their messages or events, so it offers that task too.
  return fold.byDriver.get(subject.targetId)?.[0] ?? null;
}

/**
 * Whether a task made from these rows must carry a note: one about a driver with nothing waiting says nothing on its
 * own. A task about a message or event never needs one.
 *
 * @param rows - The rows the task is made from.
 * @param grain - The grain the task is named at, from {@link taskGrainFor}.
 * @param unacknowledgedByFeed - How many things wait on each channel, for a driver named from a message's row.
 */
export function noteRequiredFor(
  rows: readonly DispatchTableRow[],
  grain: DispatchGrain,
  unacknowledgedByFeed: ReadonlyMap<string, number>,
): boolean {
  return rows.some((row) => {
    if (taskSubjectOfRow(row, grain)?.subject !== "membership") return false;
    // A driver's row counts what waits on the channel; any other row reads the same count by the driver's channel.
    if (row.kind === "driver") return row.unacknowledgedCount === 0;
    return (row.driver.feedId === null ? 0 : (unacknowledgedByFeed.get(row.driver.feedId) ?? 0)) === 0;
  });
}

/**
 * What making tasks from rows writes: a subject per new task, and the open tasks to move rather than double.
 *
 * Two rows about one target make one task, as the route allows no repeat. A message or event that already carries an
 * open task has that task moved to the chosen board; a driver can carry many tasks, so one about a driver is always
 * made. A row about nothing a task can name is skipped.
 *
 * @param rows - The rows the tasks are made from.
 * @param grain - The grain the tasks are named at, from {@link taskGrainFor}.
 * @param fold - The workspace's open tasks, to find a target's task already in force.
 */
export function taskWritesFor(
  rows: readonly DispatchTableRow[],
  grain: DispatchGrain,
  fold: TaskFold,
): { subjects: TaskSubjectWrite[]; moves: OpenTask[] } {
  const seen = new Set<string>();
  const subjects: TaskSubjectWrite[] = [];
  const moves: OpenTask[] = [];
  for (const row of rows) {
    const subject = taskSubjectOfRow(row, grain);
    if (subject === null) continue;
    // A target already seen in this selection makes no second task.
    const key = taskSubjectKey(subject.subject, subject.targetId);
    if (seen.has(key)) continue;
    seen.add(key);
    // A message or event that already carries an open task has that task moved; a driver gets another.
    const current = subject.subject === "membership" ? undefined : fold.bySubject.get(key)?.[0];
    if (current) moves.push(current);
    else subjects.push(subject);
  }
  return { subjects, moves };
}
